import { cardArtSrc } from "../shared/cards/cardFace";
import {
  GALLERY_CHAPTER_PIECES,
  isArtUnlocked,
  loadCollection,
  ownedVerseIds,
  STARTER_OWNED_IDS,
} from "../shared/collection";
import { CATALOG, DEFAULT_DECK_IDS } from "../shared/deck/catalog";
import {
  CAMPAIGNS,
  isDifficultyCleared,
  isMissionUnlocked,
  loadCampaign,
  type CampaignDef,
  type CampaignDifficulty,
  type CampaignSave,
  type MissionDef,
} from "../prototypes/proclaim/campaign";
import { openWhatsNewDialog } from "../shared/whatsNewDialog";
import { seedWhatsNewIfNeeded, unseenWhatsNew } from "../shared/whatsNew";

document.documentElement.dataset.app = "menu";

const BG_CELLS = 9; // 3×3 collage
/**
 * Campaign library (selector) — hub view, no stage map open.
 * Always use this from the main menu so players can pick any arc.
 */
const PLAY_CAMPAIGN_LIBRARY = "/prototypes/proclaim/?mode=campaign";
const PLAY_FREE = "/freeplay/";
const DIFFS: CampaignDifficulty[] = ["easy", "standard", "hard"];
const BETA_DISMISS_KEY = "sots-beta-stamp-dismissed";
const FREE_PLAY_ART = "/art/lies/whisper-1.webp";

/** Deep-link into a campaign's stage map (Continue / Next). */
function playStageMapHref(campaignId: string, missionId?: string): string {
  const u = new URL("/prototypes/proclaim/", "https://local.invalid");
  u.searchParams.set("mode", "campaign");
  u.searchParams.set("view", "missions");
  u.searchParams.set("campaign", campaignId);
  if (missionId) u.searchParams.set("mission", missionId);
  return `${u.pathname}${u.search}`;
}

/** Fisher–Yates shuffle (in place). */
function shuffleInPlace<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

function storyArtUrl(src: string): string {
  return src.startsWith("/") ? src : `/art/story/${src}`;
}

/** Prefer owned card arts for the menu collage; fall back to starter / catalog. */
function randomBackgroundVerseIds(count: number): string[] {
  loadCollection();
  let pool = ownedVerseIds();
  if (pool.length < 3) pool = [...STARTER_OWNED_IDS];
  if (pool.length === 0) pool = CATALOG.map((v) => v.id);
  if (pool.length === 0) return [...DEFAULT_DECK_IDS].slice(0, count);
  const shuffled = shuffleInPlace([...pool]);
  // Allow repeats if pool is smaller than the grid
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(shuffled[i % shuffled.length]!);
  }
  return out;
}

function renderMenuBackground(): void {
  const root = document.getElementById("menu-bg-art");
  if (!root) return;

  const seed = randomBackgroundVerseIds(BG_CELLS);
  const cols = 3;
  const rows = 3;
  const cells = cols * rows;
  const shown: string[] = [];
  for (let i = 0; i < cells; i++) {
    shown.push(seed[i % Math.max(seed.length, 1)] ?? DEFAULT_DECK_IDS[0]!);
  }

  root.style.setProperty("--menu-bg-cols", String(cols));
  root.style.setProperty("--menu-bg-rows", String(rows));
  root.replaceChildren();

  for (const id of shown) {
    const img = document.createElement("img");
    img.src = cardArtSrc(id);
    img.alt = "";
    img.decoding = "async";
    img.loading = "eager";
    img.draggable = false;
    root.appendChild(img);
  }
}

// --- Campaign home destinations (continue strip + primary cards) ---

function playableCampaigns(): CampaignDef[] {
  return CAMPAIGNS.filter(
    (c) => c.status === "playable" && (c.missions?.length ?? 0) > 0,
  );
}

function isMissionClearedAny(save: CampaignSave, missionId: string): boolean {
  return DIFFS.some((d) => isDifficultyCleared(save, missionId, d));
}

/** Story complete = finale cleared on any difficulty (not full Easy/Std/Hard mastery). */
function isCampaignStoryComplete(
  save: CampaignSave,
  camp: CampaignDef,
): boolean {
  const missions = camp.missions;
  if (!missions?.length) return false;
  const finaleId =
    camp.finaleMissionId ?? missions[missions.length - 1]!.id;
  return isMissionClearedAny(save, finaleId);
}

function campaignHasProgress(save: CampaignSave, camp: CampaignDef): boolean {
  const missions = camp.missions;
  if (!missions?.length) return false;
  if (missions.some((m) => isMissionClearedAny(save, m.id))) return true;
  // Unlocked past the first stage counts even before a clear is recorded
  return missions.some(
    (m) => m.index > 0 && isMissionUnlocked(save, m.index, camp.id),
  );
}

function nextStoryMission(
  save: CampaignSave,
  camp: CampaignDef,
): MissionDef | null {
  const missions = camp.missions;
  if (!missions?.length) return null;
  const unlocked = missions.filter((m) =>
    isMissionUnlocked(save, m.index, camp.id),
  );
  return (
    unlocked.find((m) => !isMissionClearedAny(save, m.id)) ??
    unlocked[unlocked.length - 1] ??
    missions[0]!
  );
}

type HomePlay =
  | {
      kind: "fresh";
      camp: CampaignDef;
      mission: MissionDef;
    }
  | {
      kind: "continue";
      camp: CampaignDef;
      mission: MissionDef;
    }
  | {
      kind: "next-campaign";
      camp: CampaignDef;
      mission: MissionDef;
      /** Campaigns already story-complete (for copy). */
      cleared: CampaignDef[];
    }
  | {
      kind: "all-clear";
      cleared: CampaignDef[];
    };

/**
 * Single source of truth for home CTAs.
 * Prefer: mid-arc continue → start next arc after a clear → free play when done.
 */
function resolveHomePlay(save: CampaignSave): HomePlay {
  const playable = playableCampaigns();
  const cleared = playable.filter((c) => isCampaignStoryComplete(save, c));
  const incomplete = playable.filter((c) => !isCampaignStoryComplete(save, c));

  // Mid-arc: any campaign with progress that is not story-complete
  for (const camp of incomplete) {
    if (!campaignHasProgress(save, camp)) continue;
    const mission = nextStoryMission(save, camp);
    if (!mission) continue;
    return { kind: "continue", camp, mission };
  }

  // Next unstarted arc (player has finished at least one, or none yet)
  const next = incomplete[0];
  if (next) {
    const mission = nextStoryMission(save, next) ?? next.missions![0]!;
    if (cleared.length > 0) {
      return { kind: "next-campaign", camp: next, mission, cleared };
    }
    return { kind: "fresh", camp: next, mission };
  }

  return { kind: "all-clear", cleared: playable };
}

function hasAnyCampaignProgress(save: CampaignSave): boolean {
  return playableCampaigns().some(
    (c) =>
      campaignHasProgress(save, c) || isCampaignStoryComplete(save, c),
  );
}

function shortBookLabel(camp: CampaignDef): string {
  // Prefer the scripture book when kicker carries it (e.g. "… · Nehemiah")
  const kick = camp.kicker;
  const parts = kick.split("·").map((s) => s.trim());
  const last = parts[parts.length - 1];
  if (last && last.length < 28 && !/^single/i.test(last)) return last;
  return camp.title;
}

function clearedNames(camps: CampaignDef[]): string {
  return camps.map((c) => shortBookLabel(c)).join(" · ");
}

function stageMeta(camp: CampaignDef, mission: MissionDef): string {
  const total = camp.missions?.length ?? camp.stageCount ?? "?";
  return `Stage ${mission.index + 1} of ${total}`;
}

function campaignStageProgress(
  save: CampaignSave,
  camp: CampaignDef,
): { cleared: number; total: number; ratio: number } {
  const missions = camp.missions ?? [];
  const total = missions.length || camp.stageCount || 1;
  const cleared = missions.filter((m) =>
    isMissionClearedAny(save, m.id),
  ).length;
  return {
    cleared,
    total,
    ratio: total > 0 ? Math.min(1, cleared / total) : 0,
  };
}

/** First intro panel with shipped art (Nehemiah stages); null when text-only. */
function firstStoryPanelArt(
  mission: MissionDef,
): { src: string; caption: string } | null {
  const intro = mission.story?.intro;
  if (!intro?.length) return null;
  const panel = intro.find((p) => p.artSrc) ?? intro[0];
  if (!panel?.artSrc) return null;
  const src = panel.artSrc.startsWith("/")
    ? panel.artSrc
    : `/art/story/${panel.artSrc}`;
  return {
    src,
    caption: panel.caption ?? mission.story?.title ?? mission.name,
  };
}

function continueCardHtml(opts: {
  href: string;
  className?: string;
  kicker: string;
  title: string;
  meta: string;
  /** 0–1 progress through the campaign story ladder */
  progressRatio?: number;
  /** Optional first-panel art for the stage this CTA continues/starts. */
  art?: { src: string; caption: string } | null;
}): string {
  const classes = ["continue-card", opts.className, opts.art && "continue-card-has-art"]
    .filter(Boolean)
    .join(" ");
  const thumb = opts.art
    ? `<span class="continue-thumb" aria-hidden="true">
        <img
          src="${escapeAttr(opts.art.src)}"
          alt=""
          width="160"
          height="100"
          loading="lazy"
          decoding="async"
        />
      </span>`
    : "";
  const progress =
    opts.progressRatio != null
      ? `<span class="continue-progress" aria-hidden="true">
          <span
            class="continue-progress-bar"
            style="width: ${Math.round(opts.progressRatio * 100)}%"
          ></span>
        </span>`
      : "";
  return `
    <a class="${classes}" href="${escapeAttr(opts.href)}">
      ${thumb}
      <span class="continue-body">
        <span class="continue-kicker">${escapeHtml(opts.kicker)}</span>
        <span class="continue-title">${escapeHtml(opts.title)}</span>
        <span class="continue-meta">${escapeHtml(opts.meta)}</span>
        ${progress}
      </span>
    </a>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, "&#39;");
}

/**
 * Top continue strip — only when the player has a clear next action that
 * should not compete with a duplicated campaign card below.
 */
function renderContinueStrip(
  home: HomePlay,
  hasProgress: boolean,
  save: CampaignSave,
): void {
  const root = document.getElementById("continue-strip");
  if (!root) return;

  // Brand-new: featured campaign card is enough
  if (home.kind === "fresh" || !hasProgress) {
    root.hidden = true;
    root.innerHTML = "";
    root.removeAttribute("aria-label");
    return;
  }

  root.hidden = false;

  if (home.kind === "continue") {
    const prog = campaignStageProgress(save, home.camp);
    root.setAttribute("aria-label", "Continue campaign");
    root.innerHTML = continueCardHtml({
      href: playStageMapHref(home.camp.id, home.mission.id),
      kicker: "Continue",
      title: home.mission.name,
      meta: `${home.camp.title} · ${stageMeta(home.camp, home.mission)} · ${prog.cleared}/${prog.total} cleared`,
      progressRatio: prog.ratio,
      art: firstStoryPanelArt(home.mission),
    });
    return;
  }

  if (home.kind === "next-campaign") {
    const done = clearedNames(home.cleared);
    root.setAttribute("aria-label", "Next campaign");
    root.innerHTML = continueCardHtml({
      href: playStageMapHref(home.camp.id, home.mission.id),
      className: "continue-card-next",
      kicker: "Next campaign",
      title: home.camp.title,
      meta: `${done} cleared · begin ${home.mission.name}`,
      progressRatio: 0,
      art: firstStoryPanelArt(home.mission),
    });
    return;
  }

  // All story arcs complete — celebrate and route to free play
  const done = clearedNames(home.cleared);
  const lastCleared = home.cleared[home.cleared.length - 1];
  const finale =
    lastCleared?.missions?.find((m) => m.id === lastCleared.finaleMissionId) ??
    lastCleared?.missions?.[lastCleared.missions.length - 1];
  root.setAttribute("aria-label", "Campaigns complete");
  root.innerHTML = continueCardHtml({
    href: PLAY_FREE,
    className: "continue-card-complete",
    kicker: "Story complete",
    title: "Free play · keep the edge",
    meta: `${done} cleared · Hold the Line or Muster`,
    progressRatio: 1,
    art: finale ? firstStoryPanelArt(finale) : null,
  });
}

// Keep in bundle for content-hash bust after CDN poison incidents
void (document.documentElement.dataset.assetGen = "2");

function setCampaignMedia(src: string | undefined | null): void {
  const img = document.getElementById(
    "campaign-media-img",
  ) as HTMLImageElement | null;
  if (!img || !src) return;
  img.src = storyArtUrl(src);
}

/**
 * Unlocked gallery art only — prefer chapter keepsakes, fall back to owned
 * verse faces so a brand-new save still has something to show.
 */
function unlockedGalleryTeaserSrcs(): string[] {
  loadCollection();
  const chapter = GALLERY_CHAPTER_PIECES.filter((p) => isArtUnlocked(p.id)).map(
    (p) => p.src,
  );
  // Dedupe while preferring chapter pieces first
  const seen = new Set<string>();
  const out: string[] = [];
  for (const src of chapter) {
    if (seen.has(src)) continue;
    seen.add(src);
    out.push(src);
  }
  // Verse faces fill out the pool when few/no chapter unlocks yet
  for (const id of ownedVerseIds()) {
    const src = cardArtSrc(id);
    if (seen.has(src)) continue;
    seen.add(src);
    out.push(src);
  }
  return out;
}

/** Pick two distinct unlocked plates (random each page load). */
function pickGalleryTeaserPair(): [string, string] {
  const pool = shuffleInPlace([...unlockedGalleryTeaserSrcs()]);
  if (pool.length === 0) {
    // Absolute fallback (starter deck should always unlock verse faces)
    const starter = STARTER_OWNED_IDS.map((id) => cardArtSrc(id));
    const a = starter[0] ?? FREE_PLAY_ART;
    const b = starter[1] ?? a;
    return [a, b];
  }
  const a = pool[0]!;
  const b = pool[1] ?? pool[0]!;
  return [a, b];
}

function renderGalleryTeaser(): void {
  const [srcA, srcB] = pickGalleryTeaserPair();
  const imgA = document.getElementById(
    "gallery-media-a",
  ) as HTMLImageElement | null;
  const imgB = document.getElementById(
    "gallery-media-b",
  ) as HTMLImageElement | null;
  if (imgA) imgA.src = srcA;
  if (imgB) imgB.src = srcB;
}

/**
 * Tune featured cards so home never double-sells a finished arc.
 * - Continue strip deep-links the stage map
 * - Campaign card always opens the library (no campaign selected into the map)
 * - All clear: free play featured
 */
function tuneHomeCards(home: HomePlay): void {
  const grid = document.getElementById("play-grid");
  const campaignCard = document.getElementById(
    "card-campaign",
  ) as HTMLAnchorElement | null;
  const freeCard = document.getElementById(
    "card-free",
  ) as HTMLAnchorElement | null;
  const badge = document.getElementById("campaign-badge");
  const cta = document.getElementById("campaign-cta");
  const titleEl =
    document.getElementById("campaign-title") ??
    campaignCard?.querySelector("h2") ??
    null;
  const pitchEl =
    document.getElementById("campaign-pitch") ??
    campaignCard?.querySelector(".proto-pitch") ??
    null;
  const metaEl =
    document.getElementById("campaign-meta") ??
    campaignCard?.querySelector(".proto-meta") ??
    null;
  const freeBadge =
    document.getElementById("free-badge") ??
    freeCard?.querySelector(".proto-badge") ??
    null;
  const freeCta = document.getElementById("free-cta");
  const footer = document.getElementById("menu-footer-copy");
  const lead = document.getElementById("menu-lead");

  const setFeatured = (el: Element | null, on: boolean) => {
    el?.classList.toggle("featured", on);
  };

  // Free play art (static atmosphere)
  const freeImg = freeCard?.querySelector(
    ".play-card-img",
  ) as HTMLImageElement | null;
  if (freeImg && !freeImg.src.includes("whisper")) {
    freeImg.src = FREE_PLAY_ART;
  }

  // Campaign card is always the selector (library) — never a stage-map deep link.
  if (titleEl) titleEl.textContent = "Campaigns";
  if (campaignCard) campaignCard.href = PLAY_CAMPAIGN_LIBRARY;

  if (home.kind === "fresh") {
    grid?.classList.add("is-new-player");
    setFeatured(campaignCard, true);
    setFeatured(freeCard, false);
    setCampaignMedia(home.camp.coverArt);
    if (badge) badge.textContent = "Start here";
    if (cta) cta.textContent = "Choose a campaign →";
    if (pitchEl) {
      pitchEl.textContent =
        "Pick a story path — stage maps, teaching hands, verse rewards. Best place to learn the fight.";
    }
    if (metaEl) {
      metaEl.innerHTML = `
        <li>Story ladder</li>
        <li>Easy · Standard · Hard</li>
      `;
    }
    if (freeBadge) freeBadge.textContent = "Endless";
    if (freeCta) freeCta.textContent = "Open free play →";
    if (lead) {
      lead.textContent =
        "Speak Scripture before a lie holds the Will — speech is the cost.";
    }
    // Quiet legal strip only — onboarding lives on the campaign card + in-game Pause.
    setFooterLegal(footer);
    return;
  }

  grid?.classList.remove("is-new-player");

  if (home.kind === "continue") {
    setFeatured(campaignCard, false);
    setFeatured(freeCard, false);
    setCampaignMedia(home.camp.coverArt);
    if (badge) badge.textContent = "All arcs";
    if (cta) cta.textContent = "Campaign selector →";
    if (pitchEl) {
      pitchEl.textContent = `Continue above resumes ${home.mission.name}. Open Campaigns to switch arcs, replay, or browse the library.`;
    }
    if (metaEl) {
      metaEl.innerHTML = `
        <li>All arcs</li>
        <li>${escapeHtml(home.camp.focus ?? "Story ladder")}</li>
      `;
    }
    if (freeBadge) freeBadge.textContent = "Endless";
    if (freeCta) freeCta.textContent = "Open free play →";
    if (lead) {
      lead.textContent = `${home.camp.title} · pick up where you left off.`;
    }
    setFooterLegal(footer);
    return;
  }

  if (home.kind === "next-campaign") {
    setFeatured(campaignCard, false);
    setFeatured(freeCard, false);
    setCampaignMedia(home.camp.coverArt);
    if (badge) badge.textContent = "All arcs";
    if (cta) cta.textContent = "Campaign selector →";
    if (pitchEl) {
      const done = clearedNames(home.cleared);
      pitchEl.textContent = `${done} cleared. Continue above starts ${home.camp.title}, or open Campaigns to pick any arc.`;
    }
    if (metaEl) {
      metaEl.innerHTML = `
        <li>All arcs</li>
        <li>Replay · Hard</li>
      `;
    }
    if (freeBadge) freeBadge.textContent = "Endless";
    if (freeCta) freeCta.textContent = "Open free play →";
    if (lead) {
      lead.textContent = "One wall rebuilt — the next book waits.";
    }
    setFooterLegal(footer);
    return;
  }

  // all-clear: free play is the forward door; campaign is library / replay
  setFeatured(campaignCard, false);
  setFeatured(freeCard, true);
  const last = home.cleared[home.cleared.length - 1];
  setCampaignMedia(last?.coverArt);
  if (badge) badge.textContent = "Replay";
  if (cta) cta.textContent = "Campaign selector →";
  if (pitchEl) {
    const done = clearedNames(home.cleared);
    pitchEl.textContent = `${done} complete. Open Campaigns to replay Hard, hunt difficulties, or switch arcs.`;
  }
  if (metaEl) {
    metaEl.innerHTML = `
      <li>Replay · Hard</li>
      <li>All arcs</li>
    `;
  }
  if (freeBadge) freeBadge.textContent = "Play next";
  if (freeCta) freeCta.textContent = "Open free play →";
  if (lead) {
    lead.textContent = "Story arcs cleared — keep the edge in free play.";
  }
  setFooterLegal(footer);
}

/** Minimal legal strip — no tutorial dump at the bottom of the home menu. */
function setFooterLegal(footer: HTMLElement | null): void {
  if (!footer) return;
  footer.className = "menu-footer-links";
  footer.innerHTML = `<a href="/legal/">Privacy &amp; Legal</a>`;
}

function wireBetaStamp(): void {
  const stamp = document.getElementById("beta-stamp");
  const btn = document.getElementById("btn-beta-dismiss");
  if (!stamp) return;

  try {
    if (localStorage.getItem(BETA_DISMISS_KEY) === "1") {
      stamp.hidden = true;
      return;
    }
  } catch {
    /* private mode — leave visible */
  }

  btn?.addEventListener("click", () => {
    stamp.hidden = true;
    try {
      localStorage.setItem(BETA_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  });
}

function maybeShowWhatsNew(hasProgress: boolean): void {
  seedWhatsNewIfNeeded({ hasProgress });
  const unseen = unseenWhatsNew({ hasProgress });
  if (!unseen.length) return;

  // One note at a time; Next advances; dismiss marks all as seen.
  openWhatsNewDialog({
    entries: unseen,
    markCaughtUpOnClose: true,
    closeLabel: unseen.length > 1 ? "Close all" : "Got it",
  });
}

const campaignSave = loadCampaign();
const homePlay = resolveHomePlay(campaignSave);
const progress = hasAnyCampaignProgress(campaignSave);

wireBetaStamp();
renderMenuBackground();
renderGalleryTeaser();
renderContinueStrip(homePlay, progress, campaignSave);
tuneHomeCards(homePlay);
maybeShowWhatsNew(progress);
